import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import PropTypes from "prop-types";
import { useMemo } from "react";
import { AthletesModel, getTotalAnnonced } from "../../../../util/src";
import dico from "../../../../util/src/language/dico";
import backgroundSvg from "../../img/Challenge_Hiver_Widget.svg";
import Shape from "../../misc/Shape";
import LiftPill from "./LiftPill";

interface CurrentCategoryRankingWidgetProps {
  currentAthlete: AthletesModel;
  categoryAthletes: AthletesModel[];
  placementIfSuccessful: number | null;
  isVisible: boolean;
}

const MotionTableBody = motion.create(TableBody);
const MotionTableRow = motion.create(TableRow);

const CurrentCategoryRankingWidget: React.FC<
  CurrentCategoryRankingWidgetProps
> = ({
  currentAthlete,
  categoryAthletes,
  placementIfSuccessful,
  isVisible,
}) => {
  const textStyle = {
    color: "#002439",
    fontWeight: 800,
    textTransform: "uppercase" as const,
    letterSpacing: 1,
    opacity: 1,
    position: "relative" as const,
    zIndex: 3,
    textShadow: `
      0px 2px 4px #fff,
      0px 0px 8px #fff
    `,
  };
  const placementDifferent = useMemo(() => {
    return (
      placementIfSuccessful !== null &&
      currentAthlete.placement !== placementIfSuccessful
    );
  }, [currentAthlete, placementIfSuccessful]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        gap: 1,
        position: "relative",
      }}
    >
      <style>
        {`
          @keyframes blink-current {
              0% { opacity: 1; }
              50% { opacity: 0.3; }
              100% { opacity: 1; }
          }
        `}
      </style>

      <AnimatePresence mode="wait">
        {isVisible && (
          <motion.div
            key={currentAthlete.catAgeId + "-" + currentAthlete.catPoidId}
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
              transition: { duration: 0.45, ease: [0.22, 0.61, 0.36, 1] },
            }}
            exit={{
              opacity: 0,
              y: -40,
              scale: 0.95,
              transition: {
                duration: 0.45,
                ease: [0.22, 0.61, 0.36, 1],
                delay: 5,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                position: "relative",
                alignItems: "center",
                justifyContent: "center",
                px: 6,
                py: 3,
                borderRadius: "25px",
                boxShadow: "0 18px 35px rgba(0,0,0,0.35)",
                overflow: "hidden",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  backgroundImage: `url(${backgroundSvg})`,
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  opacity: 1,
                  zIndex: 1,
                }}
              />

              <Typography variant="h3" sx={{ ...textStyle, zIndex: 2 }}>
                {currentAthlete.catPoid?.nom} {currentAthlete.catAge?.nom}
              </Typography>

              <TableContainer sx={{ zIndex: 3, padding: 1 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ borderBottom: "none" }}></TableCell>
                      <TableCell sx={{ borderBottom: "none" }}></TableCell>
                      <TableCell sx={{ borderBottom: "none" }}>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <Typography variant="h5" sx={{ ...textStyle }}>
                            {dico().widget.achievedTotal}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ borderBottom: "none" }}>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <Typography variant="h5" sx={{ ...textStyle }}>
                            {dico().widget.announcedTotal}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  {/* Corps du tableau animé */}
                  <MotionTableBody layout>
                    {categoryAthletes.map((athlete) => {
                      const placement = athlete.placement ?? 0;

                      const newPlacement =
                        athlete.id === currentAthlete.id
                          ? placementIfSuccessful
                          : (placementIfSuccessful ?? 0) <= placement
                            ? placement + 1
                            : placement;

                      const isCurrent = athlete.id === currentAthlete.id;
                      const hasImproved =
                        isCurrent &&
                        placementDifferent &&
                        ((newPlacement ?? placement) < placement ||
                          (placement === 0 &&
                            (newPlacement ?? placement) > placement));

                      return (
                        <MotionTableRow
                          key={athlete.id}
                          layout
                          initial={false}
                          transition={{
                            type: "spring",
                            stiffness: 80,
                            damping: 30,
                          }}
                          animate={{
                            backgroundColor: hasImproved
                              ? "rgba(88,193,106,0.15)"
                              : "rgba(88,193,106,0)",
                          }}
                          sx={{
                            position: "relative",
                            "&::after": {
                              content: '""',
                              zIndex: "2 !important",
                              width: "100%",
                              height: "100%",
                              position: "absolute",
                              left: 0,
                              top: 0,
                              border: isCurrent
                                ? "2px solid rgb(94,145,174)"
                                : "none",
                              borderRadius: "25px",
                              animation: isCurrent
                                ? "blink-current 2s infinite"
                                : "none",
                              backgroundColor: isCurrent
                                ? "rgba(94, 145, 174, 0.33)"
                                : "rgba(88,193,106,0)",
                            },
                          }}
                        >
                          <TableCell
                            sx={{ borderBottom: "none", paddingLeft: 0 }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Shape
                                animation={
                                  isCurrent
                                    ? "blink-current 2s infinite"
                                    : undefined
                                }
                                zIndex={3}
                                variant="triangle"
                                size={40}
                                fill={
                                  isCurrent
                                    ? "rgb(94,145,174)"
                                    : "rgba(88,193,106,0)"
                                }
                                orientation="right"
                              />
                              <Typography
                                variant="h3"
                                sx={{ ...textStyle, zIndex: 3 }}
                              >
                                {athlete.placement}
                              </Typography>

                              {placementDifferent &&
                              newPlacement !== placement ? (
                                <Shape
                                  zIndex={3}
                                  variant="triangle"
                                  orientation={
                                    (newPlacement ?? 0) < placement ||
                                    (placement === 0 &&
                                      (newPlacement ?? 0) > placement)
                                      ? "up"
                                      : "down"
                                  }
                                  fill={
                                    (newPlacement ?? 0) < placement ||
                                    (placement === 0 &&
                                      (newPlacement ?? 0) > placement)
                                      ? "rgb(88,193,106)"
                                      : "rgb(202,87,106)"
                                  }
                                  size={30}
                                />
                              ) : (
                                <Shape
                                  zIndex={3}
                                  variant="triangle"
                                  size={30}
                                />
                              )}
                            </Box>
                          </TableCell>

                          <TableCell sx={{ borderBottom: "none" }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                flexDirection: "column",
                                alignItems: "flex-start",
                              }}
                            >
                              <Typography variant="h4" sx={{ ...textStyle }}>
                                {athlete.prenom}
                              </Typography>
                              <Typography variant="h4" sx={{ ...textStyle }}>
                                {athlete.nom}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell
                            align="center"
                            sx={{ borderBottom: "none" }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                position: "relative",
                                zIndex: 3,
                              }}
                            >
                              <LiftPill
                                width={150}
                                backgroundColor="rgb(88,193,106)"
                                color="#000"
                                value={athlete.totalkg}
                              />
                            </Box>
                          </TableCell>

                          <TableCell sx={{ borderBottom: "none" }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                position: "relative",
                                zIndex: 3,
                              }}
                            >
                              <LiftPill
                                width={150}
                                value={getTotalAnnonced(athlete)}
                                color="#000"
                                backgroundColor="rgb(241,201,113)"
                              />
                            </Box>
                          </TableCell>
                        </MotionTableRow>
                      );
                    })}
                  </MotionTableBody>
                </Table>
              </TableContainer>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

CurrentCategoryRankingWidget.propTypes = {
  currentAthlete: PropTypes.any.isRequired,
  categoryAthletes: PropTypes.array.isRequired,
  placementIfSuccessful: PropTypes.number,
  isVisible: PropTypes.bool.isRequired,
};

export default CurrentCategoryRankingWidget;
